// @ts-nocheck
/* eslint-disable */
/* eslint-disable @typescript-eslint/no-floating-promises */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import React from 'react';
import type { DealerId } from '@oneaudi/dealer-context-service';
import { breakpointsMap } from '@oneaudi/unified-web-common';
import { useEnvConfigContext, AuthContext } from '@oneaudi/myaudi-kit-utils';
import { ConsentModal, saveConsentToStorage, useGoogleConsent } from '@oneaudi/two-click';
import { ProgressIndicator } from '@oneaudi/unified-web-components';
import { useWindowSize } from '../../../hooks/useWindowSize';
import MobileLayout from '../MobileLayout/MobileLayout';
import DesktopLayout from '../DesktopLayout/DesktopLayout';
import useResultsByTermAndAddress from '../../../hooks/useResultsByTermAndAddress';
import useResultsByGeoLocation from '../../../hooks/useResultsByGeoLocation';
import useResultsByGeoArea from '../../../hooks/useResultsByGeoArea';
import mapContext from '../../../contexts/map-context';
import { useGeolocation } from '../../../hooks/useMapGeolocation';
import { useMapQueryParam } from '../../../hooks/useMapQueryParams';
import { useResultByDealerId } from '../../../hooks/useResultByDealerId';
import { GlobalStyleReset } from '../../common/GlobalStyleReset';
import { TwoClickConsentWrapper } from '../../common/TwoClickConsentModal/style';
import { getFavoritePartner, getRatingSummary, getRatings, getReviews } from '../../../api';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import { useInitialQueryParams } from '../../../hooks/useInitialQueryParams';
import type { SearchParams } from '../../../models/common/searchParams';
import { generateBaseUrl } from '../../../utils/generateBaseUrl';
import { LoaderContainer } from '../../../FeatureApp';
import { useConnectDealerByGeo } from '../../../hooks/useConnectDealerToDealerContext';
import { parseConnectedDealer } from '../../../utils/connectDealer';
import { DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE } from '../../../utils/globals';

const isLegacy = process.env.IS_LEGACY;
const DEALER_SEARCH_GOOGLE_MAPS_CONSENT = 'faDealerSearchGoogleMapsConsent';
const GOOGLE_MAPS_CONSENT_DURATION_IN_DAYS = 180;

const getDealerSearchConsentExpiry = () =>
  Date.now() + GOOGLE_MAPS_CONSENT_DURATION_IN_DAYS * 24 * 60 * 60 * 1000;

const getDealerSearchGoogleMapsConsent = () => {
  if (typeof window === 'undefined') return false;

  try {
    const storedConsent = window.localStorage.getItem(DEALER_SEARCH_GOOGLE_MAPS_CONSENT);
    if (!storedConsent) return false;

    const parsedConsent = JSON.parse(storedConsent);
    return parsedConsent.consent === true && parsedConsent.expiresOn > Date.now();
  } catch {
    return false;
  }
};

const saveDealerSearchGoogleMapsConsent = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      DEALER_SEARCH_GOOGLE_MAPS_CONSENT,
      JSON.stringify({
        consent: true,
        expiresOn: getDealerSearchConsentExpiry(),
      }),
    );
  } catch {
    // Ignore storage errors; global two-click consent remains the source of truth.
  }
};

const removeDealerSearchGoogleMapsConsent = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(DEALER_SEARCH_GOOGLE_MAPS_CONSENT);
  } catch {
    // Ignore storage errors; the map will stay gated in this Feature App.
  }
};

const MapLayout: React.FC = () => {
  const windowSize = useWindowSize();
  const {
    myAudiCtaConfig,
    isMapTriggeredByTeaser,
    twoClickConsentRequired,
    twoClickData,
    locale,
    fiveStarData,
    oneHeader,
    activeMobileView,
    autoConnectDealer,
    dealerContextService,
    results: dealerResults,
    connectedDealer,
    selectedDealerId,
    headerMobileView,
  } = mapContext.useMapState();

  const { triggerGeolocationSearch } = useGeolocation();
  const { parseInitialParams } = useInitialQueryParams();
  const authService = React.useContext(AuthContext);

  const consent = useGoogleConsent(true);
  const [dealerSearchConsent, setDealerSearchConsent] = React.useState(
    getDealerSearchGoogleMapsConsent,
  );
  const isGoogleMapsConsentAccepted = !twoClickConsentRequired || (consent && dealerSearchConsent);

  const autoConnectedUnsubscribed =
    typeof window !== 'undefined' &&
    Boolean(sessionStorage.getItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE));

  useResultsByGeoArea();
  useMapQueryParam();
  useResultByDealerId();
  useResultsByTermAndAddress();
  useResultsByGeoLocation();

  useConnectDealerByGeo({
    enabled: autoConnectDealer && !autoConnectedUnsubscribed,
  });

  const dispatch = mapContext.useMapDispatch();

  const config = useEnvConfigContext<any>();

  const searchParams: SearchParams = parseInitialParams();

  const dispatchAccessToken = () => {
    dispatch({
      type: ActionTypes.setAccessToken,
      payload: authService,
    });
  };

  React.useEffect(() => {
    const unsubscribe = dealerContextService?.subscribe((id) => {
      if (!dealerContextService) return;
      const parsed = parseConnectedDealer(id as DealerId);
      if (parsed !== connectedDealer) {
        dispatch({ type: ActionTypes.setConnectedDealer, payload: parsed });
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dealerResults, connectedDealer, selectedDealerId, dealerContextService]);

  const getFavPartner = async () => {
    if (await authService.isAuthenticated()) {
      try {
        const partnerObj = await getFavoritePartner(authService, locale);

        const hasError = Object.prototype.hasOwnProperty.call(partnerObj, 'message');

        if (hasError) {
          dispatch({
            type: ActionTypes.setErrorMessage,
            payload: { code: partnerObj.code, message: partnerObj.message },
          });
        } else {
          dispatch({
            type: ActionTypes.setFavoritePartner,
            payload: partnerObj,
          });
          dispatch({
            type: ActionTypes.setAccessToken,
            payload: authService,
          });
        }
      } catch {
        // Error handling - could be logged to service in production
      }
    }
  };

  React.useEffect(() => {
    if (isMapTriggeredByTeaser) {
      triggerGeolocationSearch();
    }
  }, []);

  React.useEffect(() => {
    getFavPartner();
    dispatchAccessToken();
  }, [authService, config]);

  React.useEffect(() => {
    const searchParamsDealerId = searchParams['dealer-id'];

    const updateFiveStarResults = () => {
      const ReviewsPromise = getReviews(searchParamsDealerId || '');
      const RatingSummaryPromise = getRatingSummary(searchParamsDealerId || '');
      Promise.all([ReviewsPromise, RatingSummaryPromise]).then((results) => {
        const [dealerReviews, ratingSummary] = results;
        dispatch({ type: ActionTypes.setDealerReviews, payload: dealerReviews });
        dispatch({ type: ActionTypes.setRatingSummary, payload: ratingSummary });
        if (searchParams.reviews) {
          setTimeout(() => {
            dispatch({ type: ActionTypes.setIsFlyoutOpen, payload: true });
          });
        }
      });
    };

    const updateDealerRatings = async () => {
      const dealerRatings = await getRatings();
      dispatch({ type: ActionTypes.setDealerRatings, payload: { dealerRatings } });
    };

    if (fiveStarData.displayFiveStarRating) {
      updateDealerRatings();
      if (searchParamsDealerId) {
        updateFiveStarResults();
      }
    }
  }, []);

  const revokeConsent = () => {
    removeDealerSearchGoogleMapsConsent();
    setDealerSearchConsent(false);
    setTimeout(() => {
      saveConsentToStorage(false);
    }, 1000);
  };

  const handleConsentModalClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const confirmButton = target.closest<HTMLButtonElement>('#confirm-button');

    if (confirmButton && !confirmButton.disabled) {
      saveDealerSearchGoogleMapsConsent();
      setDealerSearchConsent(true);
    }
  };

  const showMyAudiCloseButton = myAudiCtaConfig
    ? Object.prototype.hasOwnProperty.call(myAudiCtaConfig, 'cancelCta')
    : false;

  const desktopBackgroundImg = `${generateBaseUrl()}${isLegacy ? '/desktop_map.png' : '/desktop_map_alpha.png'}`;
  const mobileBackgroundImg = `${generateBaseUrl()}${isLegacy ? '/mobile_map.png' : '/mobile_map_alpha.png'}`;

  return (
    <GlobalStyleReset headerMobileView={headerMobileView} oneHeader={oneHeader}>
      <TwoClickConsentWrapper
        desktopImg={desktopBackgroundImg}
        mobileImg={mobileBackgroundImg}
        isDesktop={windowSize.width > parseInt(breakpointsMap.md, 10)}
        oneHeader={oneHeader}
        isListView={activeMobileView === 'list'}
        isLegacy={isLegacy}
      >
        {typeof window !== 'undefined' ? (
          <>
            {!isGoogleMapsConsentAccepted ? (
              <div onClickCapture={handleConsentModalClickCapture}>
                <ConsentModal
                  theme="light"
                  headerText={twoClickData.headline}
                  bodyText={twoClickData.copyText}
                  toggleText={twoClickData.toggleLabel}
                  redirectButtonText={twoClickData.closeButton?.text || 'Back to homepage'}
                  redirectButtonURL={twoClickData.closeButton?.redirectURL || '/'}
                  showRedirectButton={!showMyAudiCloseButton}
                  showCloseButton={showMyAudiCloseButton}
                  closeButtonCallback={
                    myAudiCtaConfig?.cancelCta && myAudiCtaConfig.cancelCta.clickHandler
                  }
                  isDealerSearch
                  demo
                />
              </div>
            ) : windowSize.width < parseInt(breakpointsMap.md, 10) || oneHeader ? (
              <MobileLayout data-test-id="component-mobile-layout" revokeConsent={revokeConsent} />
            ) : (
              <DesktopLayout
                data-test-id="component-desktop-layout"
                revokeConsent={revokeConsent}
              />
            )}
          </>
        ) : (
          <LoaderContainer>
            <ProgressIndicator
              variant="spinner"
              type="infinite"
              data-test-id="mobile-layout-component"
            />
          </LoaderContainer>
        )}
      </TwoClickConsentWrapper>
    </GlobalStyleReset>
  );
};

export default MapLayout;
